import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";

import styles from "./Combobox.module.css";

export interface ComboOption<T> {
  key: string;
  primary: string;
  secondary?: string;
  data: T;
}

export interface ComboboxProps<T> {
  label: string;
  help?: string;
  placeholder?: string;
  /** Text of the chosen value, or null when nothing is chosen. */
  value: string | null;
  /** Query key prefix for TanStack Query (the search term is appended). */
  queryKey: string;
  search: (term: string) => Promise<ComboOption<T>[]>;
  onSelect: (option: ComboOption<T>) => void;
  onClear?: () => void;
  clearLabel?: string;
  noneText: string;
  searchingText: string;
  minLength?: number;
  footer?: ReactNode;
  error?: string | null;
}

function useDebounced(value: string, delay = 200): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, delay);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, delay]);
  return debounced;
}

/** Search-as-you-type picker (ARIA 1.2 combobox): ↑/↓ moves, Enter or a click chooses, Esc closes. */
export function Combobox<T>({
  label,
  help,
  placeholder,
  value,
  queryKey,
  search,
  onSelect,
  onClear,
  clearLabel,
  noneText,
  searchingText,
  minLength = 2,
  footer,
  error,
}: ComboboxProps<T>) {
  const id = useId();
  const [text, setText] = useState(value ?? "");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const term = useDebounced(text.trim());
  const showList = open && term.length >= minLength && term !== value;
  const results = useQuery({
    queryKey: [queryKey, term],
    queryFn: () => search(term),
    enabled: showList,
  });
  const options = results.data ?? [];

  useEffect(() => {
    setText(value ?? "");
  }, [value]);

  function choose(option: ComboOption<T>) {
    onSelect(option);
    setText(option.primary);
    setOpen(false);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!showList || options.length === 0) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) => (current + step + options.length) % options.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = options[active];
      if (option) choose(option);
    }
  }

  const listId = `${id}-list`;
  const describedBy = [help ? `${id}-help` : null, error ? `${id}-error` : null].filter(Boolean).join(" ");
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      {help && (
        <p id={`${id}-help`} className={styles.help}>
          {help}
        </p>
      )}
      <div className={styles.row}>
        <input
          id={id}
          className={styles.input}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          aria-activedescendant={showList && options[active] ? `${listId}-${active}` : undefined}
          autoComplete="off"
          placeholder={placeholder}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            // Give a click on an option the time to land first.
            window.setTimeout(() => {
              setOpen(false);
              setText(value ?? "");
            }, 150);
          }}
        />
        {value && onClear && clearLabel && (
          <button
            type="button"
            className={styles.clear}
            onClick={() => {
              onClear();
              setText("");
            }}
          >
            {clearLabel}
          </button>
        )}
      </div>
      {showList && (
        <div id={listId} role="listbox" className={styles.listbox} aria-label={label}>
          {results.isFetching && options.length === 0 && <div className={styles.note}>{searchingText}</div>}
          {!results.isFetching && options.length === 0 && <div className={styles.note}>{noneText}</div>}
          {options.map((option, index) => (
            <div
              key={option.key}
              id={`${listId}-${index}`}
              role="option"
              tabIndex={-1}
              aria-selected={index === active}
              className={[styles.option, index === active && styles.optionActive].filter(Boolean).join(" ")}
              onMouseDown={(event) => {
                event.preventDefault();
                choose(option);
              }}
            >
              <span>{option.primary}</span>
              {option.secondary && <span className={styles.secondary}>{option.secondary}</span>}
            </div>
          ))}
        </div>
      )}
      {error && (
        <p id={`${id}-error`} className={styles.error} role="alert">
          {error}
        </p>
      )}
      {footer}
    </div>
  );
}
